import React from 'react';

import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';

import { checkIfOffline, toErrorMessage } from '_helpers/queryInterceptorHelpers';

import type { FallbackProps } from 'react-error-boundary';

import './DefaultFallback.css';

/**
 * Default error boundary fallback UI.
 * Displays an error message and provides a button to refresh the page.
 */
function DefaultFallback({error}: FallbackProps) {
  const errorString: string = toErrorMessage(error) || 'An unknown error occurred.';
  const isOffline: boolean = checkIfOffline(error);

  // Report non-offline errors to Sentry without an eager bundling of Sentry.
  React.useEffect(() => {
    if (isOffline) return;
    (async () => {
      try {
        const Sentry = await import('@sentry/react');
        Sentry.captureException(error);
      } catch {
        // swallow reporting errors
      }
    })();
  }, [error, isOffline]);

  return (
    <Container>
      <Card className="default-fallback-card">
        <Card.Header className="text-center">
          <h2>{isOffline ? 'Lost internet connection' : 'Oops! Something went wrong.'}</h2>
        </Card.Header>
        <Card.Body className="text-center">
          {isOffline ? (
            <>
              <p>
                Don't worry! Your progress is saved.
              </p>
              <p>
                Please check your connection and refresh the page when you're back online.
              </p>
            </>
          ) : (
            <>
              <p>
                We're sorry for the inconvenience. Our technical team has been notified.
              </p>
              <p>
                <strong>Please refresh the page and try again.</strong>
              </p>
            </>
          )}
          <Button onClick={() => window.location.reload()} variant="primary">
            Refresh the Page
          </Button>
        </Card.Body>
        <Card.Footer>
          <p><strong>Error Details:</strong></p>
          <pre className="text-muted small mb-0">{errorString}</pre>
        </Card.Footer>
      </Card>
    </Container>
  )
}

export default DefaultFallback;